import { X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useParent } from './ParentContext';

const KEY = 'cg.billingNoticeHidden';
const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: 'long', day: 'numeric' });

/**
 * Billing notices in the app (#871), for the family's owner: the trial ending (until they choose how to pay), a yearly
 * renewal coming, or a failed payment. "Not now" hides one until the next one comes along.
 */
export function BillingNoticeBanner() {
  const { parent } = useParent();
  const notice = parent.billing?.notice;
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(KEY) ?? '';
    } catch {
      return '';
    }
  });
  if (!notice || hidden === notice.ref) return null;
  const hide = () => {
    try {
      localStorage.setItem(KEY, notice.ref);
    } catch {
      /* private mode: just this visit */
    }
    setHidden(notice.ref);
  };
  const days = notice.date ? Math.max(1, Math.ceil((notice.date - Date.now()) / 86_400_000)) : 0;
  return (
    <div className={`billing-notice ${notice.kind}`} role="status">
      {notice.kind === 'trial' ? (
        <span>
          <strong>Your free trial ends {day(notice.date!)}</strong> ({days} day{days === 1 ? '' : 's'} left). Choose how to pay to keep going, or do nothing and you won’t be charged.
        </span>
      ) : notice.kind === 'price' ? (
        <span>
          <strong>
            Your price changes to ${((notice.amountCents ?? 0) / 100).toFixed(2)} a {notice.interval ?? 'month'} from {day(notice.date!)}.
          </strong>{' '}
          Nothing changes before then.
        </span>
      ) : notice.kind === 'renewal' ? (
        <span>
          <strong>
            Your plan renews {day(notice.date!)}
            {notice.amountCents ? ` for $${(notice.amountCents / 100).toFixed(2)}` : ''}.
          </strong>{' '}
          Cancel before then if you don’t want it to.
        </span>
      ) : (
        <span>
          <strong>Your last payment didn’t go through.</strong> Update your card so nothing is interrupted.
        </span>
      )}
      <Link className={`btn small ${notice.kind === 'renewal' || notice.kind === 'price' ? '' : 'primary'}`} to="/settings#plan">
        {notice.kind === 'trial' ? 'Choose how to pay' : notice.kind === 'renewal' ? 'Manage billing' : notice.kind === 'price' ? 'Review your plan' : 'Update card'}
      </Link>
      {notice.kind !== 'failed' && (
        <button type="button" className="icon-btn" onClick={hide} aria-label="Not now">
          <X size={16} />
        </button>
      )}
    </div>
  );
}
